import React from "react";
import PropTypes from "prop-types";
import { Popover } from "antd";

/**
 * Fraud indicator count with a hover breakdown.
 *
 * The count alone says nothing actionable, so hovering lists every indicator the
 * analyser checked — flagged ones first, each with its trigger count and details.
 * Indicators that were checked and came back clean are still listed (muted), so a
 * zero reads as "checked, nothing found" rather than "not checked".
 *
 * A missing `indicators` array means the analyser never returned the section
 * (older analyses do not carry it) — that renders as "--", NOT as a zero, because
 * "no fraud found" and "never checked" are different claims.
 *
 * `stat` renders at the summary-header scale (matching EditableAnalysisField);
 * without it the value inherits the surrounding table cell. `small` renders
 * at EditableAnalysisField's sub-row scale — used when this cell is stacked
 * below another metric (e.g. under Credit Limit) rather than standing alone.
 */
const FraudIndicatorsCell = ({ count, indicators, loading, stat, small }) => {
    const baseClass =
        "bsa-fraud"
        + (stat ? " bsa-fraud--stat" : "")
        + (small ? " bsa-fraud--small" : "");

    if (loading) {
        return <span className={`${baseClass} bsa-fraud--muted`}>...</span>;
    }
    const list = Array.isArray(indicators) ? indicators : null;
    if (!list) {
        return <span className={`${baseClass} bsa-fraud--muted`}>--</span>;
    }

    const flaggedCount =
        count != null ? Number(count) : list.filter((i) => i.identified).length;
    const hasFlag = flaggedCount > 0;

    // Flagged first, so the hover leads with what matters.
    const ordered = [...list].sort(
        (a, b) => Number(b.identified) - Number(a.identified)
    );

    const content = (
        <div className="bsa-fraud-pop">
            {ordered.map((item, i) => (
                <div
                    key={i}
                    className={
                        "bsa-fraud-pop__row"
                        + (item.identified ? " bsa-fraud-pop__row--flagged" : "")
                    }
                >
                    <div className="bsa-fraud-pop__head">
                        <span className="bsa-fraud-pop__name">
                            {item.indicator}
                        </span>
                        {item.triggers_count ? (
                            <span className="bsa-fraud-pop__count">
                                {item.triggers_count}
                            </span>
                        ) : null}
                    </div>
                    {item.details ? (
                        <div className="bsa-fraud-pop__details">
                            {item.details}
                        </div>
                    ) : null}
                </div>
            ))}
        </div>
    );

    return (
        <Popover
            content={content}
            title={`Fraud indicators (${list.length} checked)`}
            placement="bottomLeft"
            overlayClassName="bsa-fraud-overlay"
        >
            <span className={baseClass + (hasFlag ? " bsa-fraud--flagged" : "")}>
                {flaggedCount}
            </span>
        </Popover>
    );
};

FraudIndicatorsCell.propTypes = {
    count: PropTypes.number,
    indicators: PropTypes.array,
    loading: PropTypes.bool,
    stat: PropTypes.bool,
    small: PropTypes.bool,
};

export default FraudIndicatorsCell;
